import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'
import { useRequests } from '../requests/useRequests'
import {
  validateUsername,
  validatePassword,
  validatePhone,
  validateMail,
  validatePasswords
} from '../utils/validators'
import '../styles/auth.css'

const RegisterPage = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const requestMaker = useRequests()

  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [phone, setPhone] = useState('')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordAgainError, setPasswordAgainError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleRegister = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setIsLoading(true)

    if (validateUsername(username, setUsernameError) &&
        validatePassword(password, setPasswordError) &&
        validatePassword(passwordAgain, setPasswordAgainError) &&
        validatePhone(phone, setPhoneError) &&
        validateMail(email, setEmailError) &&
        validatePasswords(password, passwordAgain, setPasswordMatchError)) {
      try {
        await requestMaker.registerUser({
          username,
          password,
          phoneNumber: phone,
          email,
          captchaToken: '' // TODO: Add captcha
        })

        dispatch(RefreshActions.setToastMessage({ message: 'Konto zostało utworzone. Sprawdź swoją skrzynkę email i kliknij w link aktywacyjny.' }))
        navigate('/login?type=new-account')
      } catch (e) {
        setUsernameError('Użytkownik z tymi danymi już istnieje')
      }
    }

    setIsLoading(false)
  }

  const handleUsernameBlur = async (username: string): Promise<void> => {
    if (username === '') return

    const response = await requestMaker.emailOrUsernameExists(username)

    if (response.data) {
      setUsernameError('Użytkownik o takiej nazwie już istnieje')
    } else {
      setUsernameError(null)
    }
  }

  const handleEmailBlur = async (email: string): Promise<void> => {
    if (email === '') return

    const response = await requestMaker.emailExists(email)

    if (response.data) {
      setEmailError('Email jest już używany')
    } else {
      setEmailError(null)
    }
  }

  const handlePhoneBlur = async (phone: string): Promise<void> => {
    if (phone === '') return

    const response = await requestMaker.phoneNumberExists(phone)

    if (response.data) {
      setPhoneError('Ten numer telefonu jest już używany')
    } else {
      setPhoneError(null)
    }
  }

  return (
    <div className='login-page-wrapper'>
      <div className='login-left-panel'>
        <img src='/assets/images/logo/logo-black.png' alt='kreatlo logo' className='login-logo' />
        <h2>Już tylko jeden krok dzieli Cię od własnego konta w aplikacji Kreatlo</h2>
        <p>
          Korzystaj z wszystkich dostępnych funkcjonalności za darmo przez 30 dni!
        </p>
      </div>
      <div className='login-right-panel'>
        <div className='auth-container'>
          <h1 className='auth-title'>Rejestracja</h1>
          <p className='auth-description'>
            Dołącz do nas, aby korzystać z pełni możliwości naszej platformy.
          </p>

          <form onSubmit={handleRegister} className='auth-form'>
            <div className='form-group'>
              <input
                type='text'
                className={`form-control ${usernameError ? 'error' : ''}`}
                placeholder='Nazwa użytkownika'
                value={username}
                onChange={(e) => {
                  setUsernameError(null)
                  setUsername(e.target.value)
                }}
                onBlur={async (e) => await handleUsernameBlur(e.target.value)}
                required
              />
              {usernameError && <div className='error-text'>{usernameError}</div>}
            </div>

            <div className='form-group'>
              <input
                type='email'
                className={`form-control ${emailError ? 'error' : ''}`}
                placeholder='Email'
                value={email}
                onChange={(e) => {
                  setEmailError(null)
                  setEmail(e.target.value)
                }}
                onBlur={async (e) => await handleEmailBlur(e.target.value)}
                required
              />
              {emailError && <div className='error-text'>{emailError}</div>}
            </div>

            <div className='form-group'>
              <input
                type='tel'
                className={`form-control ${phoneError ? 'error' : ''}`}
                placeholder='Telefon'
                value={phone}
                onChange={(e) => {
                  setPhoneError(null)
                  setPhone(e.target.value)
                }}
                onBlur={async (e) => await handlePhoneBlur(e.target.value)}
                required
              />
              {phoneError && <div className='error-text'>{phoneError}</div>}
            </div>

            <div className='form-group'>
              <input
                type='password'
                className={`form-control ${passwordError ? 'error' : ''}`}
                placeholder='Hasło'
                value={password}
                onChange={(e) => {
                  setPasswordError(null)
                  setPasswordMatchError(null)
                  setPassword(e.target.value)
                }}
                required
              />
              {passwordError && <div className='error-text'>{passwordError}</div>}
            </div>

            <div className='form-group'>
              <input
                type='password'
                className={`form-control ${passwordAgainError || passwordMatchError ? 'error' : ''}`}
                placeholder='Powtórz hasło'
                value={passwordAgain}
                onChange={(e) => {
                  setPasswordAgainError(null)
                  setPasswordMatchError(null)
                  setPasswordAgain(e.target.value)
                }}
                required
              />
              {passwordAgainError && <div className='error-text'>{passwordAgainError}</div>}
              {passwordMatchError && <div className='error-text'>{passwordMatchError}</div>}
            </div>

            <div className='form-options'>
              <div className='custom-checkbox'>
                <input
                  type='checkbox'
                  id='acceptTerms'
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  required
                />
                <label htmlFor='acceptTerms'>
                  Akceptuję <a href='/rules' target='_blank' rel='noopener noreferrer' className='terms-link'>regulamin <i className='fas fa-external-link-alt' /></a>
                </label>
              </div>
            </div>

            <button
              type='submit'
              className='btn-primary'
              disabled={isLoading || !username || !email || !phone || !password || !passwordAgain || !termsAccepted}
            >
              {isLoading ? 'Rejestrowanie...' : 'Zarejestruj się'}
            </button>
          </form>

          <div className='auth-footer'>
            <span className='footer-text'>Masz już konto?</span>
            <button className='register-link' onClick={() => navigate('/login')}>
              Zaloguj się <i className='fas fa-arrow-right' />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
